import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { mdiChevronDown } from '@mdi/js';
import Icon from '@mdi/react';
import { headingFontBold, bodyFont, vars } from '../../../../Theme';
import { useTranslation } from 'react-i18next';
import DocumentRow, { IRelatedDocument } from './DocumentRow';

const accordionSx = {
    boxShadow: 'none',
    border: `1px solid ${vars.lightGray}`,
    fontSize: '16px',
    borderRadius: '4px',
};
const accordionSummarySx = {
    '& .MuiAccordionSummary-content': { m: 0 },
    '& .MuiAccordionSummary-content.Mui-expanded': {
        m: 0,
    },
    fontFamily: headingFontBold,
    py: '8px',
    px: '12px',
    fontSize: '16px',
    m: 0,
    minHeight: '40px',
};

const getDocumentRowSx = (isLastChild: boolean) => {
    return {
        px: '12px',
        py: '8px',
        borderBottom: isLastChild ? 'none' : `1px solid ${vars.lightGray}`,
    };
};

export default ({
    documents,
    removeDocument,
    title,
    titleIcon,
    defaultExpanded,
    children,
}: IRelatedDocumentsComponent) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'dialogParts' });

    const defaultTitle = `${t('forTheFollowingDocuments', {
        numberOfDocuments: documents.length,
    })} `;

    const renderDocuments = () => {
        if (children) return children;

        if (documents?.length > 0) {
            return documents.map((document, index) => (
                <Box
                    key={document.universalId}
                    sx={getDocumentRowSx(index === documents.length - 1)}
                >
                    <DocumentRow document={document} removeDocument={removeDocument} />
                </Box>
            ));
        }

        return <Box sx={{ p: '10px 12px', color: vars.font }}>{t('documentsGroupEmpty')}</Box>;
    };

    return (
        <Accordion disableGutters sx={accordionSx} defaultExpanded={defaultExpanded}>
            <AccordionSummary
                sx={accordionSummarySx}
                expandIcon={<Icon path={mdiChevronDown} color={vars.font} size={1} />}
            >
                {titleIcon && (
                    <Icon
                        path={titleIcon.path}
                        color={titleIcon.color}
                        size={1}
                        style={{ marginRight: '8px' }}
                    />
                )}
                {title ?? defaultTitle}
            </AccordionSummary>
            <AccordionDetails sx={{ ...bodyFont, p: 0 }}>
                {renderDocuments()}
            </AccordionDetails>
        </Accordion>
    );
};

export interface IRelatedDocumentsComponent extends React.PropsWithChildren {
    documents: IRelatedDocument[];
    removeDocument?: (documentUniId: string) => void;
    title?: string;
    titleIcon?: {
        path: string;
        color: string;
    };
    defaultExpanded?: boolean;
}
